import React, { FC, useState } from 'react'
import { useResponsive } from '@utils/hooks'
import { BgImageHOC, TitleHOC } from '@components/checkout'
import { Typography } from '@mui/material'
import { Price } from '@components/ui'
import { EstimateLine } from '@common/models/estimateLine/estimateLine'
import { withProps } from '@utils/hoc'

const PriceWrapper = withProps(Typography, { className: 'text-xl font-bold whitespace-nowrap' })

export type DeliveryMenuCardProps = {
  items: Array<EstimateLine>
}

interface IGroupMenu {
  color: string
  img: string
  name?: string
  options: Array<EstimateLine>
  quantity: number
  subtotal: number
  custom: boolean
  expired: boolean
  additionalOptions: Array<EstimateLine>
}

const DeliveryMenuCard: FC<DeliveryMenuCardProps> = ({ items }) => {
  const responsive = useResponsive(640).responsive

  const [groupMenu] = useState<IGroupMenu>(() => {
    // get name , color , url
    const first: EstimateLine = items[0]
    let {
      // @ts-ignore
      expirationDateTime,
      planning: {
        packageGroup: { color: groupColor, label, photos }
      },
      variant
    } = first

    // Check if color is white and set to a darker color
    if (groupColor === '#FFFFFF' || groupColor === '#ffffff') {
      groupColor = '#666666'
    }

    const { url } = (photos && photos[0]) || {}
    const groupImg = url || ''

    // get quantity
    const groupQuantity = items.reduce((accumulateur, valeurCourante) => accumulateur + valeurCourante.quantity, 0)

    const additionnalOptions = items.reduce((accumulateur, valeurCourante) => {
      const children = (valeurCourante.children ?? []).filter(child => typeof child.id !== 'undefined' && typeof child.totalPriceIncludingTaxes !== 'undefined')
      return [...accumulateur, ...(children ?? [])]
    }, [])

    // get price
    const optionGroupSubtotal = items.reduce((accumulateur, valeurCourante) => accumulateur + valeurCourante.totalPriceIncludingTaxes.amount, 0)
    const additionalOptionGroupSubtotal = additionnalOptions.reduce((accumulateur, valeurCourante) => accumulateur + valeurCourante.totalPriceIncludingTaxes?.amount ?? 0, 0)

    // check expiration date
    if (typeof expirationDateTime === 'string') {
      expirationDateTime = new Date(expirationDateTime)
    }

    return {
      color: groupColor,
      name: label,
      img: groupImg,
      quantity: groupQuantity,
      subtotal: optionGroupSubtotal + additionalOptionGroupSubtotal,
      expired: Date.now() > expirationDateTime.getTime(),
      options: items,
      custom: !!variant,
      additionalOptions: additionnalOptions
    }
  })

  const { name = '', color = '', custom, expired = false, quantity = 0, subtotal = 0, img, options = [], additionalOptions = [] } = groupMenu || {}

  const BgImage = BgImageHOC(color)
  const Title = TitleHOC(color)

  const getResume = () => {
    if (expired) {
      return <Typography className="mt-[5px] text-right text-super-ya-red">Hors délai</Typography>
    } else {
      return (
        <Typography>
          <Price price={subtotal} Wrapper={PriceWrapper} />
        </Typography>
      )
    }
  }

  return (
    <div className="flex flex-col gap-y-[10px] sm:gap-y-[15px]">
      <div className="border-super-ya-gray1 rounded-lg border-[1px] p-[15px] sm:px-[30px] sm:py-[20px]">
        <div className="flex items-stretch justify-between gap-x-[20px]">
          <BgImage className="h-[50px] w-[50px] sm:h-[80px] sm:w-[80px]">
            <img src={img} alt="menu image" width={responsive ? 50 : 80} height={responsive ? 50 : 80} className="min-h-[50px] min-w-[50px] sm:h-[80px] sm:w-[80px]" />
          </BgImage>
          <div className="mr-auto flex flex-shrink flex-col items-start justify-between">
            <div className="whitespace-normal">
              <Title>{!custom ? quantity + ' x ' + name : name + ' - Sur mesure'}</Title>
            </div>
            <div>
              <ul className="flex flex-wrap justify-between gap-x-[20px]">
                {options.map(option => (
                  <li key={option.id}>
                    <Typography>
                      {option.quantity} x {!custom ? option.package?.label : option.variant?.article?.label}
                    </Typography>
                  </li>
                ))}
                {additionalOptions.map(option => {
                  return (
                    <li key={option.id}>
                      <Typography>
                        {option.quantity} x {option.label}
                      </Typography>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
          <div className="flex flex-col justify-start sm:justify-end">{getResume()}</div>
        </div>
      </div>
    </div>
  )
}

export default DeliveryMenuCard
